import { PictureElement } from '@/pictureElement/Picture';
import { rewriteImageSrc } from '@/utils/rewriteImageSrc';
import { FloatingPortal, autoUpdate, shift, useFloating, useMergeRefs } from '@floating-ui/react';
import classNames from 'classnames';
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ButtonIcon,
  NAVBAR_MENU_ITEM_BUTTON_DEFAULTS,
  NAVBAR_MENU_ITEM_LINK_DEFAULTS,
  NAVBAR_MENU_ITEM_LOGO_DEFAULTS,
  NAVBAR_MENU_ITEM_SEARCH_DEFAULTS,
} from '..';
import CaretDown from '../icon/CaretDown';
import Image from '../icon/Image';
import MagnifyingGlass from '../icon/MagnifyingGlass';
import UserCircle from '../icon/UserCircle';
import { getBackgroundColorStyle } from '../utils/background';
import { cn } from '../utils/cn';
import { computeColor } from '../utils/colors';
import { computeFont } from '../utils/fonts';
import { getFontSizeVariables } from '../utils/getFontSizeVariables';
import { getHeightStyle, getWidthStyle } from '../utils/sizing';
import { DropdownProvider } from './DropdownProvider';
import * as styles from './Navbar.css';
import type {
  NavbarMenuItemButtonElementProps,
  NavbarMenuItemElementProps,
  NavbarMenuItemLinkElementProps,
  TNavbarItemButtonElement,
} from './Navbar.type';
import { useDropdownContext } from './useDropdownContext';

export function NavbarMenuItemLink(props: NavbarMenuItemLinkElementProps | NavbarMenuItemButtonElementProps) {
  const { element, className, children, onClick, attributes, ...rest } = props;

  const {
    type,
    href,
    isCustomLink,
    label,
    new_tab,
    customAttributes,
    padding,
    background,
    borderRadius,
    color,
    borderColor,
    borderWidth,
    borderStyle,
    hoverBorderColor,
    hoverBorderStyle,
    hoverBorderWidth,
    hoverBorderRadius,
    hoverTextColor,
    hoverBackgroundColor,
    hoverUnderline,
  } = element.attrs || {};

  const {
    icon = undefined,
    iconPlacement = NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.iconPlacement,
    iconColor = NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.iconColor,
    iconSize = NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.iconSize,
    hoverIconColor = iconColor,
  } = element.attrs?.type === 'button' ? element.attrs || {} : {};

  const { fontFamily, fontSize, fontWeight } = element.attrs || {};
  // Type cast only to fix type error
  const { letterSpacing } = (element.attrs as TNavbarItemButtonElement['attrs']) || {};

  const visible = element.attrs?.visible ?? true;

  const cleanHref = useMemo(() => {
    try {
      const hrefURL = href ? new URL(href) : undefined;
      if (href && hrefURL && !isCustomLink) {
        return href.replace(hrefURL.origin, '');
      }
    } catch (_e) {}
    return href;
  }, [href, isCustomLink]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!onClick) return;
      e.preventDefault();
      onClick();
    },
    [onClick],
  );

  if (element.attrs?.type !== 'link' && element.attrs?.type !== 'button') {
    return null;
  }

  if (!visible) {
    return null;
  }

  const DEFAULTS = type === 'button' ? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS : NAVBAR_MENU_ITEM_LINK_DEFAULTS;
  const classStyle = type === 'button' ? styles.navbarItemBase : styles.navbarItemLink;

  let buttonStyles: CSSProperties = {};

  if (type === 'button') {
    buttonStyles = {
      ...buttonStyles,
      '--border-color': borderColor ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.borderColor,
      '--border-width': borderWidth ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.borderWidth,
      '--border-style': borderStyle ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.borderStyle,
      '--background-color': getBackgroundColorStyle(background ?? DEFAULTS.background).background,
    } as CSSProperties;
  }

  const isNoText = !label || label?.length === 0;

  return (
    <div className={styles.navbarItemLinkContainer} {...attributes}>
      <a
        {...rest}
        {...customAttributes}
        onClick={handleClick}
        className={classNames(classStyle, className, customAttributes?.className)}
        style={
          {
            ...buttonStyles,
            '--border-radius': borderRadius ?? DEFAULTS.borderRadius,
            '--padding': padding ?? DEFAULTS.padding,
            '--text-color': computeColor(color ?? DEFAULTS.color),
            '--font-family': computeFont(fontFamily) || DEFAULTS.fontFamily,
            ...getFontSizeVariables(fontSize ?? DEFAULTS.fontSize),
            '--font-weight': fontWeight ?? DEFAULTS.fontWeight,
            '--letter-spacing': letterSpacing ?? DEFAULTS.letterSpacing,

            '--hover-border-radius': hoverBorderRadius || borderRadius || DEFAULTS.borderRadius,
            '--hover-text-color': hoverTextColor || color || DEFAULTS.color,
            '--hover-background-color': hoverBackgroundColor || background || DEFAULTS.background,
            '--hover-border-color': hoverBorderColor || borderColor || DEFAULTS.borderColor,
            '--hover-border-style': hoverBorderStyle || borderStyle || DEFAULTS.borderStyle,
            '--hover-border-width': hoverBorderWidth || borderWidth || DEFAULTS.borderWidth,

            '--hover-underline': hoverUnderline ? 'underline' : 'none',
          } as CSSProperties
        }
        href={cleanHref}
        target={new_tab ? '_blank' : '_self'}
      >
        {icon && iconPlacement === 'left' ? (
          <ButtonIcon
            icon={icon}
            color={iconColor}
            hoverColor={hoverIconColor}
            data-placement={iconPlacement}
            data-no-text={isNoText}
            className={cn('p-0', styles.icon)}
            size={iconSize}
          />
        ) : null}
        <span className="flex-1">{label}</span>
        {icon && iconPlacement === 'right' ? (
          <ButtonIcon
            color={iconColor}
            hoverColor={hoverIconColor}
            icon={icon}
            data-placement={iconPlacement}
            data-no-text={isNoText}
            className={cn('p-0', styles.icon)}
            size={iconSize}
          />
        ) : null}
      </a>
      {children}
    </div>
  );
}

export function NavbarMenuItemUserButton(
  props: NavbarMenuItemElementProps & {
    controlbyselected: boolean | string;
  },
) {
  const { element, className, onClick } = props;
  if (element.attrs?.type !== 'user_button') {
    return null;
  }
  const {
    label,
    href,
    isCustomLink,
    new_tab,
    avatar,
    firstName,
    lastName,
    email,
    customAttributes,
    padding,
    background,
    borderRadius,
    color,
    fontFamily,
    style,
    fontSize,
    fontWeight,
    width,
    height,
    letterSpacing,
    borderColor,
    borderWidth,
    borderStyle,
    hoverBorderColor,
    hoverBorderStyle,
    hoverBorderWidth,
    hoverBorderRadius,
    hoverTextColor,
    hoverBackgroundColor,
  } = element.attrs || {};

  const classStyle = styles.navbarItemButton;

  const action = element.attrs?.action;
  const visible = element.attrs?.visible ?? true;

  const buttonStyles = {
    '--border-color': borderColor ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.borderColor,
    '--border-width': borderWidth ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.borderWidth,
    '--border-style': borderStyle ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.borderStyle,
    '--background-color': getBackgroundColorStyle(background ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.background).background,
  };

  if (!visible) {
    return null;
  }

  const cleanHref = useMemo(() => {
    try {
      const hrefURL = href ? new URL(href) : undefined;
      if (href && hrefURL && !isCustomLink) {
        return href.replace(hrefURL.origin, '');
      }
    } catch (_e) {}
    return href;
  }, [href, isCustomLink]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!onClick) return;
      e.preventDefault();
      onClick();
    },
    [onClick],
  );

  const imgSrc = avatar ? rewriteImageSrc(avatar) : avatar;
  const image = avatar ? (
    <PictureElement src={imgSrc}>
      <img src={imgSrc} alt="Avatar" className={styles.navbarItemUserButtonAvatar} />
    </PictureElement>
  ) : (
    <UserCircle size={typeof width === 'number' ? width : 32} weight="fill" />
  );

  const DEFAULTS = NAVBAR_MENU_ITEM_LINK_DEFAULTS;
  if (action === 'profile') {
    if (style === 'button') {
      return (
        <a
          {...props}
          {...customAttributes}
          onClick={handleClick}
          className={classNames(classStyle, className, customAttributes?.className)}
          style={
            {
              '--padding': padding ?? '0px',
              '--border-radius': borderRadius ?? DEFAULTS.borderRadius,
              '--text-color': computeColor(color ?? DEFAULTS.color),
              '--font-family': computeFont(fontFamily) || DEFAULTS.fontFamily,
              '--letter-spacing': letterSpacing ?? DEFAULTS.letterSpacing,
              '--width': width ? getWidthStyle(width).width : '32px',
              '--height': height ? getHeightStyle(height).height : '32px',
              '--max-width': width ? getWidthStyle(width).maxWidth : '32px',
              width: 'fit-content',

              '--hover-border-radius': hoverBorderRadius || borderRadius || DEFAULTS.borderRadius,
              '--hover-text-color': hoverTextColor || color || DEFAULTS.color,
              '--hover-background-color': hoverBackgroundColor || background || DEFAULTS.background,
              '--hover-border-color': hoverBorderColor || borderColor || DEFAULTS.borderColor,
              '--hover-border-style': hoverBorderStyle || borderStyle || DEFAULTS.borderStyle,
              '--hover-border-width': hoverBorderWidth || borderWidth || DEFAULTS.borderWidth,
            } as CSSProperties
          }
          href={cleanHref}
          target={new_tab ? '_blank' : '_self'}
          title={!firstName ? email : `${firstName}${lastName ? ` ${lastName}` : ''}`}
        >
          {image}
        </a>
      );
    }

    return (
      <NavbarMenuItemDropdown
        {...props}
        showCaret={false}
        labelComponent={
          <div
            style={
              {
                '--padding': padding ?? '0px',
                '--border-radius': borderRadius ?? DEFAULTS.borderRadius,
                '--text-color': computeColor(color ?? DEFAULTS.color),
                '--font-family': computeFont(fontFamily) || DEFAULTS.fontFamily,
                '--letter-spacing': letterSpacing ?? DEFAULTS.letterSpacing,
                '--width': width ? getWidthStyle(width).width : '32px',
                '--height': height ? getHeightStyle(height).height : '32px',
                '--max-width': width ? getWidthStyle(width).maxWidth : '32px',
                width: 'fit-content',

                '--hover-border-radius': hoverBorderRadius || borderRadius || DEFAULTS.borderRadius,
                '--hover-text-color': hoverTextColor || color || DEFAULTS.color,
                '--hover-background-color': hoverBackgroundColor || background || DEFAULTS.background,
                '--hover-border-color': hoverBorderColor || borderColor || DEFAULTS.borderColor,
                '--hover-border-style': hoverBorderStyle || borderStyle || DEFAULTS.borderStyle,
                '--hover-border-width': hoverBorderWidth || borderWidth || DEFAULTS.borderWidth,
              } as CSSProperties
            }
          >
            {image}
          </div>
        }
      />
    );
  }

  const BUTTON_DEFAULTS = NAVBAR_MENU_ITEM_BUTTON_DEFAULTS;
  return (
    <a
      {...props}
      {...customAttributes}
      className={classNames(classStyle, className, customAttributes?.className)}
      style={
        {
          '--padding': padding ?? BUTTON_DEFAULTS.padding,
          '--border-radius': borderRadius ?? BUTTON_DEFAULTS.borderRadius,
          ...getBackgroundColorStyle(background ?? BUTTON_DEFAULTS.background),
          '--text-color': computeColor(color ?? BUTTON_DEFAULTS.color),
          '--font-family': computeFont(fontFamily) || BUTTON_DEFAULTS.fontFamily,
          ...getFontSizeVariables(fontSize ?? BUTTON_DEFAULTS.fontSize),
          '--font-weight': fontWeight ?? BUTTON_DEFAULTS.fontWeight,
          '--letter-spacing': letterSpacing ?? BUTTON_DEFAULTS.letterSpacing,
          ...buttonStyles,

          '--hover-border-radius': hoverBorderRadius || borderRadius || BUTTON_DEFAULTS.borderRadius,
          '--hover-text-color': hoverTextColor || color || BUTTON_DEFAULTS.color,
          '--hover-background-color': hoverBackgroundColor || background || BUTTON_DEFAULTS.background,
          '--hover-border-color': hoverBorderColor || borderColor || BUTTON_DEFAULTS.borderColor,
          '--hover-border-style': hoverBorderStyle || borderStyle || BUTTON_DEFAULTS.borderStyle,
          '--hover-border-width': hoverBorderWidth || borderWidth || BUTTON_DEFAULTS.borderWidth,
        } as CSSProperties
      }
      href={cleanHref}
      target={new_tab ? '_blank' : '_self'}
    >
      {label}
    </a>
  );
}

const NavbarMenuItemDropdownContent = ({ children }: { children: React.ReactNode }) => {
  const { open } = useDropdownContext();
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | string>(0);
  // Observe content height changes
  useEffect(() => {
    if (!contentRef.current) return;

    const updateHeight = () => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.clientHeight);
      }
    };

    // Create observer to watch for height changes in children
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(contentRef.current);
    updateHeight();

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div
      className={styles.navbarItemDropdownContentContainer}
      style={{
        maxHeight: open ? contentHeight : '0px',
        height: open ? contentHeight : '0px',
        opacity: open ? 1 : 0,
        overflow: open ? 'unset' : 'hidden',
      }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
};

export function NavbarMenuItemDropdown(
  props: NavbarMenuItemElementProps & {
    controlbyselected: boolean | string;
    labelComponent?: React.ReactNode;
    showCaret?: boolean;
  },
) {
  const {
    element,
    className,
    children,
    iframeRef,
    screen,
    controlbyselected: controlbyselectedProp,
    labelComponent,
    showCaret = true,
  } = props;
  const {
    label,
    customAttributes,
    padding,
    background,
    borderRadius,
    color,
    showOnMobile,

    borderColor,
    borderWidth,
    borderStyle,
    hoverBorderColor,
    hoverBorderStyle,
    hoverBorderWidth,
    hoverBorderRadius,
    hoverTextColor,
    hoverBackgroundColor,
  } = element.attrs || {};

  let fontFamily: CSSProperties['fontFamily'] | undefined;
  let fontSize: CSSProperties['fontSize'] | undefined;
  let fontWeight: CSSProperties['fontWeight'] | undefined;
  let letterSpacing: CSSProperties['letterSpacing'] | undefined;
  if (element.attrs?.type !== 'logo') {
    fontFamily = element.attrs?.fontFamily;
    fontSize = element.attrs?.fontSize;
    fontWeight = element.attrs?.fontWeight;
    letterSpacing = (element.attrs as TNavbarItemButtonElement['attrs'])?.letterSpacing;
  }

  const { setParentSelected } = useDropdownContext();

  let controlbyselected = controlbyselectedProp;
  if (typeof controlbyselected === 'string') {
    controlbyselected = controlbyselected === 'true';
  }

  const DEFAULTS = NAVBAR_MENU_ITEM_LINK_DEFAULTS;

  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!nodeRef.current) return;

    // Function to update the state when the dataset changes
    const updateSelected = () => {
      setParentSelected(
        nodeRef.current?.dataset?.nodeSelected === 'true' ||
          (nodeRef.current?.dataset?.childNodeSelected === 'true' &&
            nodeRef.current?.dataset?.selectedOnScreen === screen),
      );
    };

    // Create a MutationObserver to watch for changes in attributes
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'data-node-selected' || mutation.attributeName === 'data-child-node-selected')
        ) {
          updateSelected();
        }
      }
    });

    observer.observe(nodeRef.current, { attributes: true });

    // Initial setting from the dataset
    updateSelected();

    return () => {
      observer.disconnect();
    };
  }, [setParentSelected, screen]);

  const { open } = useDropdownContext();

  const { x, y, refs, strategy } = useFloating({
    open,
    strategy: 'fixed',
    placement: 'bottom',
    middleware: [shift()],
    whileElementsMounted: autoUpdate,
  });

  const mergedNodeSetRef = useMergeRefs([refs.setReference, nodeRef]);

  if (element.attrs?.type !== 'dropdown' && element.attrs?.type !== 'user_button') {
    return null;
  }
  if (element.attrs?.type === 'user_button' && element.attrs?.style === 'button') {
    return null;
  }

  // ======= Mobile =======

  if (screen === 'mobile' && !showOnMobile) {
    return (
      <div className={styles.navbarItemDropdownContainer}>
        <div
          {...props}
          role="presentation"
          {...customAttributes}
          className={classNames(styles.navbarItemDropdown, className, customAttributes?.className)}
          onClick={() => setParentSelected((o) => !o)}
          style={
            {
              '--padding': padding ?? DEFAULTS.padding,
              '--border-radius': borderRadius ?? DEFAULTS.borderRadius,
              ...getBackgroundColorStyle(background ?? DEFAULTS.background),
              '--text-color': computeColor(color ?? DEFAULTS.color),
              '--font-family': computeFont(fontFamily) || DEFAULTS.fontFamily,
              '--letter-spacing': letterSpacing ?? DEFAULTS.letterSpacing,
              ...getFontSizeVariables(fontSize ?? DEFAULTS.fontSize),
              '--font-weight': fontWeight ?? DEFAULTS.fontWeight,

              '--hover-border-radius': hoverBorderRadius || borderRadius || DEFAULTS.borderRadius,
              '--hover-text-color': hoverTextColor || color || DEFAULTS.color,
              '--hover-background-color': hoverBackgroundColor || background || DEFAULTS.background,
              '--hover-border-color': hoverBorderColor || borderColor || DEFAULTS.borderColor,
              '--hover-border-style': hoverBorderStyle || borderStyle || DEFAULTS.borderStyle,
              '--hover-border-width': hoverBorderWidth || borderWidth || DEFAULTS.borderWidth,
            } as CSSProperties
          }
        >
          {labelComponent || label}
          {showCaret && <CaretDown size={16} weight="bold" className="text-wb-secondary" />}
        </div>
        <NavbarMenuItemDropdownContent>{children}</NavbarMenuItemDropdownContent>
      </div>
    );
  }

  // ======= Desktop =======

  return (
    <div className={cn(styles.navbarItemLinkContainer, styles.navbarItemDropdownContainer)}>
      <div
        {...props}
        role="presentation"
        {...customAttributes}
        className={classNames(styles.navbarItemDropdown, className, customAttributes?.className)}
        onMouseEnter={controlbyselected ? undefined : () => setParentSelected(true)}
        onMouseLeave={controlbyselected ? undefined : () => setParentSelected(false)}
        style={
          {
            '--padding': padding ?? DEFAULTS.padding,
            '--border-radius': borderRadius ?? DEFAULTS.borderRadius,
            ...getBackgroundColorStyle(background ?? DEFAULTS.background),
            '--text-color': computeColor(color ?? DEFAULTS.color),
            '--font-family': computeFont(fontFamily) || DEFAULTS.fontFamily,
            '--letter-spacing': letterSpacing ?? DEFAULTS.letterSpacing,
            ...getFontSizeVariables(fontSize ?? DEFAULTS.fontSize),
            '--font-weight': fontWeight ?? DEFAULTS.fontWeight,

            '--hover-border-radius': hoverBorderRadius || borderRadius || DEFAULTS.borderRadius,
            '--hover-text-color': hoverTextColor || color || DEFAULTS.color,
            '--hover-background-color': hoverBackgroundColor || background || DEFAULTS.background,
            '--hover-border-color': hoverBorderColor || borderColor || DEFAULTS.borderColor,
            '--hover-border-style': hoverBorderStyle || borderStyle || DEFAULTS.borderStyle,
            '--hover-border-width': hoverBorderWidth || borderWidth || DEFAULTS.borderWidth,
          } as CSSProperties
        }
        ref={mergedNodeSetRef}
      >
        {labelComponent || label}
        {showCaret && <CaretDown size={16} weight="bold" className="text-wb-secondary" />}
      </div>

      <div className={styles.navbarItemSeparator} />
      {open && (
        <FloatingPortal root={iframeRef?.current?.contentWindow?.document.body}>
          <div
            ref={refs.setFloating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              zIndex: 9999,
            }}
          >
            {children}
          </div>
        </FloatingPortal>
      )}
    </div>
  );
}

export function NavbarMenuItemSearch(props: NavbarMenuItemElementProps) {
  const { element, className, children, onClick } = props;
  if (element.attrs?.type !== 'search') {
    return null;
  }
  const {
    customAttributes,
    padding,
    background,
    borderRadius,
    color,
    fontFamily,
    letterSpacing,
    borderColor,
    borderWidth,
    borderStyle,
    hoverBorderColor,
    hoverBorderStyle,
    hoverBorderWidth,
    hoverBorderRadius,
    hoverTextColor,
    hoverBackgroundColor,
  } = element.attrs || {};

  const DEFAULTS = NAVBAR_MENU_ITEM_SEARCH_DEFAULTS;

  return (
    <div className={styles.navbarItemLinkContainer}>
      <div
        {...props}
        onClick={(e) => {
          onClick?.(e);
        }}
        role="presentation"
        {...customAttributes}
        className={classNames(styles.navbarItemSearch, className, customAttributes?.className)}
        style={
          {
            '--padding': padding ?? DEFAULTS.padding,
            '--border-radius': borderRadius ?? DEFAULTS.borderRadius,
            '--background-color': getBackgroundColorStyle(background ?? DEFAULTS.background).background,
            '--text-color': computeColor(color ?? DEFAULTS.color),
            '--font-family': fontFamily ?? DEFAULTS.fontFamily,
            '--letter-spacing': letterSpacing ?? DEFAULTS.letterSpacing,

            '--hover-border-radius': hoverBorderRadius || borderRadius || DEFAULTS.borderRadius,
            '--hover-text-color': hoverTextColor || color || DEFAULTS.color,
            '--hover-background-color': hoverBackgroundColor || background || DEFAULTS.background,
            '--hover-border-color': hoverBorderColor || borderColor || DEFAULTS.borderColor,
            '--hover-border-style': hoverBorderStyle || borderStyle || DEFAULTS.borderStyle,
            '--hover-border-width': hoverBorderWidth || borderWidth || DEFAULTS.borderWidth,
            color: computeColor(color ?? DEFAULTS.color),
          } as CSSProperties
        }
      >
        <MagnifyingGlass size={16} weight="bold" />
        <span className={styles.navbarItemSearchText}>Search</span>
      </div>
      {children}
    </div>
  );
}

export function NavbarMenuItemLogo(props: NavbarMenuItemElementProps) {
  const { element, className, children, onClick } = props;

  if (element.attrs?.type !== 'logo') {
    return null;
  }
  const {
    customAttributes,
    href,
    new_tab,
    isCustomLink,
    src,
    width,
    height,
    padding,
    borderRadius,
    background,

    borderColor,
    borderWidth,
    borderStyle,
    hoverBorderColor,
    hoverBorderStyle,
    hoverBorderWidth,
    hoverBorderRadius,
    hoverBackgroundColor,
  } = element.attrs || {};

  const DEFAULTS = NAVBAR_MENU_ITEM_LOGO_DEFAULTS;

  const Comp = href ? 'a' : 'div';

  const cleanHref = useMemo(() => {
    try {
      const hrefURL = href ? new URL(href) : undefined;
      if (href && hrefURL && !isCustomLink) {
        return href.replace(hrefURL.origin, '');
      }
    } catch (_e) {}
    return href;
  }, [href, isCustomLink]);

  const finalWidth = width ? getWidthStyle(width).width : DEFAULTS.width;
  const finalMaxWidth = width ? getWidthStyle(width).maxWidth : DEFAULTS.width;
  const finalHeight = height ? getHeightStyle(height).height : DEFAULTS.height;
  const imgSrc = src ? rewriteImageSrc(src) : src;

  const image = imgSrc ? (
    <PictureElement src={imgSrc}>
      <img src={imgSrc} alt={'Logo'} className={styles.navbarItemLogoImage} />
    </PictureElement>
  ) : (
    <Image size={typeof height === 'number' ? height : 36} fill={'#a3a3a3'} />
  );

  return (
    <div className={styles.navbarItemLinkContainer}>
      <Comp
        {...props}
        onClick={(e) => {
          if (onClick) {
            e?.preventDefault();
            onClick();
          }
        }}
        role="presentation"
        {...customAttributes}
        className={classNames(styles.navbarItemLogo, className, customAttributes?.className)}
        style={
          {
            '--width': finalWidth === 'fit-content' || finalWidth === '100%' ? 'auto' : finalWidth, // fit-content for img defaults to fill on safari
            '--height': finalHeight === 'fit-content' ? 'auto' : finalHeight,
            '--max-width': finalMaxWidth === 'fit-content' ? 'auto' : finalMaxWidth,
            '--min-width': 'min(36px, var(--height))',
            '--padding': padding ?? DEFAULTS.padding,
            '--border-radius': borderRadius ?? DEFAULTS.borderRadius,

            '--hover-border-radius': hoverBorderRadius || borderRadius || DEFAULTS.borderRadius,
            '--hover-background-color': hoverBackgroundColor || background || DEFAULTS.background,
            '--hover-border-color': hoverBorderColor || borderColor || DEFAULTS.borderColor,
            '--hover-border-style': hoverBorderStyle || borderStyle || DEFAULTS.borderStyle,
            '--hover-border-width': hoverBorderWidth || borderWidth || DEFAULTS.borderWidth,
          } as CSSProperties
        }
        href={cleanHref}
        target={new_tab ? '_blank' : '_self'}
      >
        {image}
      </Comp>
      {children}
    </div>
  );
}

export function NavbarMenuItem(
  props: NavbarMenuItemElementProps & {
    controlbyselected: boolean | string;
    selected: boolean;
  },
) {
  const { element, children } = props;
  const { type } = element.attrs || {};

  if (type === 'link' || type === 'button') {
    return (
      <NavbarMenuItemLink {...(props as NavbarMenuItemLinkElementProps | NavbarMenuItemButtonElementProps)}>
        <div className={styles.navbarItemSeparator} />
      </NavbarMenuItemLink>
    );
  }
  if (type === 'user_button') {
    return (
      <DropdownProvider>
        <NavbarMenuItemUserButton {...props}>
          {children}
          {element?.attrs?.type === 'user_button' && element?.attrs?.style === 'button' && (
            <div className={styles.navbarItemSeparator} />
          )}
        </NavbarMenuItemUserButton>
      </DropdownProvider>
    );
  }
  if (type === 'search') {
    return (
      <NavbarMenuItemSearch {...props}>
        {children}
        <div className={styles.navbarItemSeparator} />
      </NavbarMenuItemSearch>
    );
  }
  if (type === 'logo') {
    return (
      <NavbarMenuItemLogo {...props}>
        {children}
        <div className={styles.navbarItemSeparator} />
      </NavbarMenuItemLogo>
    );
  }
  return (
    <DropdownProvider>
      <NavbarMenuItemDropdown {...props}>
        {children}
        {/* Separator is inside the NavbarMenuItemDropdown component */}
      </NavbarMenuItemDropdown>
    </DropdownProvider>
  );
}
